import { toast } from 'components/ui/toast-store'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useOutletContext } from 'react-router-dom'
import routes from 'constants/routes'
import type maplibregl from 'maplibre-gl'
import type { EvaluationOutletContext } from '../Evaluations_ShowCN'
import { Card, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import {
	Plus,
	Building2,
	MapPin,
	AlertCircle,
	RefreshCw,
	Sparkles,
	Trophy,
} from 'lucide-react'
import { cn } from 'lib/utils'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { formatSyncSummary } from 'utils/portfolioSync'
import { fetchSites } from 'slices/evaluationSiteSlice'
import { syncEvaluationWithPortfolio, setSelectedSite } from 'slices/evaluationSlice'
import EvaluationSiteForm from './EvaluationSiteForm'
import EvaluationAddExistingSitePopup from '../popups/EvaluationAddExistingSitePopup'
import { toMappableSites, toPreciseSites } from '../utils/siteMapPosition'
import useOtherRetailersForSites from 'features/OtherRetailers/useOtherRetailersForSites'
import OtherRetailPanel, {
	type OtherRetailItem,
} from 'screens/locations/components/Locations/OtherRetailPanel'
import OtherRetailScanDialog from 'screens/locations/components/Locations/OtherRetailScanDialog'
import SiteEditSheet from './SiteEditSheet'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import {
	MapContainer,
	SiteMarkers,
	MapLegend,
	MapLayersPanel,
	TradeAreaRings,
	OverlapZones,
	DemographicHeatmap,
	OtherRetailersLayer,
	useMapLayerState,
	tradeAreaService,
	type SiteForRings,
	type RingVisibilityConfig,
	type SiteForOverlap,
} from 'features/Map'

// Per-site avatar: shows the linked Location's image when available, falls back
// to a sequential letter badge (A, B, C…) based on the site's position in the
// sorted list. Tracks its own image-load error state so a bad URL degrades to
// the letter fallback without affecting siblings.
function SiteAvatar({ imageUrl, letter }: { imageUrl?: string | null; letter: string }) {
	const [errored, setErrored] = useState(false)
	const showImage = imageUrl && !errored
	return (
		<div className="w-10 h-10 rounded-lg overflow-hidden shrink-0">
			{showImage ? (
				<img
					src={imageUrl!}
					alt=""
					loading="lazy"
					onError={() => setErrored(true)}
					className="w-10 h-10 object-cover"
				/>
			) : (
				<div className="w-10 h-10 flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] font-semibold text-sm">
					{letter}
				</div>
			)}
		</div>
	)
}

const GEOCODE_COLORS: Record<string, string> = {
	SUCCESS: 'bg-green-100 text-green-700',
	PROVIDED: 'bg-blue-100 text-blue-700',
	PENDING: 'bg-gray-100 text-gray-700',
	NOT_FOUND: 'bg-amber-100 text-amber-700',
	ERROR: 'bg-red-100 text-red-700',
}

const EvaluationSites = () => {
	const { evalId, evaluation } = useOutletContext<EvaluationOutletContext>()
	const dispatch = useDispatch()
	const [createOpen, setCreateOpen] = useState(false)
	const [addExistingOpen, setAddExistingOpen] = useState(false)
	const [syncing, setSyncing] = useState(false)
	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message, variant }: { message: string; variant: 'success' | 'destructive' }) =>
		variant === 'success' ? toast.success(message, { title: 'Portfolio synced' }) : toast.error(message, { title: 'Sync failed' })
	const [editSheetSite, setEditSheetSite] = useState<any>(null)
	const [mapSite, setMapSite] = useState<any>(null)
	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [isMapLoaded, setIsMapLoaded] = useState(false)
	// Per-radius rings model: each radius from TRADE_AREA_RINGS is an
	// independent toggle in the "Rings" submenu. Default empty (no rings
	// shown until the user picks at least one). Shared with LocationsMapPanel
	// since MYD-486; this block was a verbatim duplicate of that one.
	const {
		showMarkers,
		toggleMarkers,
		enabledRadii,
		toggleRadius,
		showOverlaps,
		toggleOverlaps,
		showDemographics,
		toggleDemographics,
	} = useMapLayerState()

	const [showOtherRetailers, setShowOtherRetailers] = useState(true)

	/**
	 * Which candidate the Other Retail panel and the scan act on.
	 *
	 * Local UI state, and deliberately NOT `evaluation.selectedSite`: that is
	 * the WINNER behind the trophy toggle, and it is usually unset while
	 * somebody is still comparing candidates, which is exactly when nearby
	 * retail matters most. Tying the scan to it would leave the feature with no
	 * anchor on the screens it exists for.
	 */
	const [focusedSiteId, setFocusedSiteId] = useState<string | null>(null)

	const { entities, byEvaluation, isLoading } = useSelector((state: any) => state.evaluationSites)

	useEffect(() => {
		// @ts-ignore
		dispatch(fetchSites(evalId))
	}, [dispatch, evalId])

	const sites = useMemo(() => {
		const ids: string[] = byEvaluation[evalId] || []
		return ids.map((id) => entities[id]).filter(Boolean)
	}, [byEvaluation, entities, evalId])

	// Every site we can put a pin on. A site whose address failed to geocode
	// falls back to the centre of its named city/postal code rather than
	// vanishing from the map.
	const mappableSites = useMemo(() => toMappableSites(sites), [sites])

	// Rings and overlaps use precise sites only — see the note on
	// toPreciseSites for why an approximate site gets a pin but no catchment.
	const preciseSites = useMemo(() => toPreciseSites(sites), [sites])

	const approximateCount = mappableSites.length - preciseSites.length
	const unplaceableCount = sites.length - mappableSites.length

	const ringSites: SiteForRings[] = useMemo(
		() =>
			preciseSites.map((s: any) => ({
				id: s._id,
				latitude: s.latitude,
				longitude: s.longitude,
			})),
		[preciseSites],
	)

	const overlapSites: SiteForOverlap[] = ringSites

	const ringVisibility: RingVisibilityConfig = useMemo(() => {
		const out: RingVisibilityConfig = {}
		for (const s of preciseSites) {
			out[s._id as string] = enabledRadii
		}
		return out
	}, [preciseSites, enabledRadii])

	/**
	 * Other Retailers, per candidate site, unioned on the map.
	 *
	 * Only precise sites are passed. An approximate placement is a city or
	 * postal-code centroid, and `toPreciseSites` already withholds rings from
	 * those for a reason that applies doubly to retail: "within 1 mile" of a
	 * postal-code centroid measures nothing.
	 */
	const retailers = useOtherRetailersForSites({
		sites: preciseSites as any[],
		ringVisibility,
		focusedSiteId,
		enabled: true,
	})

	// Default the focus to the first precise site, and follow the list if that
	// site is removed. Without this the panel would sit empty on arrival even
	// though there is an obvious candidate to show.
	useEffect(() => {
		const ids = preciseSites.map((s: any) => String(s._id))
		if (ids.length === 0) {
			if (focusedSiteId !== null) setFocusedSiteId(null)
			return
		}
		if (!focusedSiteId || !ids.includes(focusedSiteId)) setFocusedSiteId(ids[0])
	}, [preciseSites, focusedSiteId])

	const focusedSite = useMemo(
		() => preciseSites.find((s: any) => String(s._id) === focusedSiteId) ?? null,
		[preciseSites, focusedSiteId],
	)

	/**
	 * The focused site's saved retailers, mapped to the panel's shape.
	 *
	 * Same mapping `LocationsMapPanel` does, so the panel stays a presentational
	 * component that any surface can feed.
	 */
	const focusedRetailItems: OtherRetailItem[] = useMemo(
		() =>
			retailers.focusedItems.map((r: any) => ({
				id: r._id,
				name: r.name,
				category: r.category,
				brand: r.brandName,
				address: r.address,
				city: r.city,
				stateProvince: r.stateProvince,
				postalCode: r.postalCode,
				latitude: r.latitude,
				longitude: r.longitude,
				distanceMiles: r.distanceMiles,
				image: r.logoUrl ?? null,
				monogram: r.monogram,
				monogramColor: r.monogramColor,
				source: r.source,
			})),
		[retailers.focusedItems],
	)

	// Auto-fit the embedded map so the full extent of the outermost trade-
	// area rings is visible — not just the site points. Fits over every
	// mappable site, approximate ones included, so a site placed by its city
	// centre is still inside the initial view.
	useEffect(() => {
		if (!map || !isMapLoaded || mappableSites.length === 0) return
		const bounds = tradeAreaService.getBoundsForSitesWithRings(
			mappableSites.map((s: any) => ({ longitude: s.longitude, latitude: s.latitude })),
		)
		map.fitBounds(bounds as maplibregl.LngLatBoundsLike, {
			padding: 40,
			duration: 600,
			maxZoom: 13,
		})
	}, [map, isMapLoaded, mappableSites])

	// Truthy for both the populated portfolio object and a bare ObjectId, so it
	// survives whichever shape the detail endpoint returns.
	const hasPortfolio = Boolean(evaluation?.portfolio)

	// The winning site. Tolerates both a populated document and a bare
	// ObjectId, since the list and detail endpoints differ.
	const selectedSiteId = evaluation?.selectedSite
		? String((evaluation.selectedSite as any)?._id ?? evaluation.selectedSite)
		: null

	// Approval promotes the winner into the Locations catalogue, so changing
	// it afterwards would orphan that Location. The server enforces this too.
	const winnerLocked =
		evaluation?.status === 'APPROVED' || evaluation?.status === 'ARCHIVED'

	const handleToggleWinner = useCallback(
		async (siteId: string) => {
			// No optimistic update: the server can reject the change, and a
			// phantom crown on a locked evaluation is worse than a short wait.
			const result: any = await (dispatch as any)(
				setSelectedSite({
					evalId,
					siteId: selectedSiteId === siteId ? null : siteId,
				}),
			)
			if (result?.meta?.requestStatus !== 'fulfilled') {
				showToast({
					message:
						typeof result?.payload === 'string'
							? result.payload
							: 'Could not update the winning site.',
					variant: 'destructive',
				})
			}
		},
		[dispatch, evalId, selectedSiteId],
	)

	const siteActionButtons = (
		<>
			<Button
				variant="primary"
				onClick={() => setAddExistingOpen(true)}
				className="gap-1.5"
				title="Pick a location from your catalogue, or a site from another evaluation"
			>
				<Plus className="w-4 h-4" />
				Add Existing
			</Button>
			<Button
				variant="primary"
				onClick={() => setCreateOpen(true)}
				className="gap-1.5"
				title="Create a brand new site, by address or by dropping a pin on the map"
			>
				<Sparkles className="w-4 h-4" />
				Create New
			</Button>
		</>
	)

	const handleSync = useCallback(async () => {
		setSyncing(true)
		try {
			const result: any = await (dispatch as any)(syncEvaluationWithPortfolio(evalId))
			if (result?.meta?.requestStatus !== 'fulfilled') {
				showToast({
					message:
						typeof result?.payload === 'string'
							? result.payload
							: 'Portfolio sync failed. Please try again.',
					variant: 'destructive',
				})
				return
			}
			// Mandatory: the sync has no reducer in evaluationSlice and sites live
			// in a different slice, so byEvaluation[evalId] is stale until this
			// lands. fetchSites replaces the bucket wholesale, which picks up both
			// the additions and the unlinked sites in one pass. Awaited so the list
			// has repainted before the toast claims what changed.
			await (dispatch as any)(fetchSites(evalId))
			showToast({
				message: formatSyncSummary(result.payload),
				variant: 'success',
			})
		} finally {
			setSyncing(false)
		}
	}, [dispatch, evalId])

	const syncButton = (
		<Button
			variant="ghost"
			isOutline
			onClick={handleSync}
			disabled={syncing}
			className="gap-1.5"
			title="Pull the linked portfolio's locations in as sites"
		>
			<RefreshCw className={cn('w-4 h-4', syncing && 'animate-spin')} />
			{syncing ? 'Syncing...' : 'Sync Portfolio'}
		</Button>
	)

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
						<Building2 className="w-5 h-5 text-[#0080fe]" />
						Sites
					</h3>
					<p className="text-sm text-gray-500">
						{sites.length} candidate {sites.length === 1 ? 'site' : 'sites'}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{hasPortfolio && syncButton}
					{siteActionButtons}
				</div>
			</div>

			{isLoading && sites.length === 0 ? (
				<div className="text-center py-12 text-sm text-gray-400">Loading sites...</div>
			) : sites.length === 0 ? (
				<div className="text-center py-12">
					<Building2 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
					<p className="text-sm text-gray-500 mb-4">
						{hasPortfolio
							? 'No sites yet. Sync the linked portfolio, or add a candidate location by hand.'
							: 'No sites yet. Add candidate locations to begin scoring.'}
					</p>
					<div className="flex items-center justify-center gap-2">
						{hasPortfolio && syncButton}
						{siteActionButtons}
					</div>
				</div>
			) : (
				<div className="grid grid-cols-6 gap-4 h-[calc(100vh-280px)] min-h-[500px]">
					{/* Left column — site cards (1/6) */}
					<div className="col-span-1 overflow-y-auto space-y-2 pr-1">
						{sites.map((site: any, index: number) => {
							const addressLine = [site.address, site.city, site.state, site.postalCode]
								.filter(Boolean)
								.join(', ')
							const letter = String.fromCharCode(65 + index)
							const isWinner = selectedSiteId === String(site._id)
							return (
								<Card
									key={site._id}
									className={cn(
										'overflow-hidden cursor-pointer hover:shadow-sm transition-shadow',
										isWinner && 'ring-2 ring-[#0080fe]',
									)}
									onClick={() => setEditSheetSite(site)}
								>
									<CardContent className="p-4">
										<div className="flex items-start gap-3">
											<SiteAvatar imageUrl={site.location?.image || site.coverImage} letter={letter} />
											<div className="min-w-0 flex-1">
												<div className="flex items-start justify-between gap-2 flex-wrap">
													<Link
														to={`${routes.ALL_SITES}/${site._id}`}
														onClick={ev => ev.stopPropagation()}
														className="font-semibold text-sm text-gray-900 truncate no-underline hover:text-[#0080fe] hover:underline"
													>
														{site.name}
													</Link>
													<div className="flex shrink-0 items-center gap-1">
														{/* Winning site. This is the one that gets
														    promoted into the Locations catalogue when
														    the evaluation is approved, so it is frozen
														    from that point on. */}
														{winnerLocked ? (
															isWinner && (
																<Badge
																	className="gap-1 bg-[#0080fe]/10 text-[10px] text-[#0080fe]"
																	title="Locked — the evaluation has been approved"
																>
																	<Trophy className="h-3 w-3" />
																	Winning site
																</Badge>
															)
														) : (
															<button
																type="button"
																onClick={(e) => {
																	// The card itself opens the edit sheet.
																	e.stopPropagation()
																	handleToggleWinner(String(site._id))
																}}
																title={
																	isWinner
																		? 'Winning site — click to unselect'
																		: 'Mark as the winning site'
																}
																aria-pressed={isWinner}
																className={cn(
																	'rounded-md p-1 transition-colors',
																	isWinner
																		? 'bg-[#0080fe]/10 text-[#0080fe]'
																		: 'text-gray-300 hover:bg-gray-100 hover:text-gray-500',
																)}
															>
																<Trophy className="h-3.5 w-3.5" />
															</button>
														)}
														{site.geocodeStatus && (
															<Badge
																className={cn(
																	'text-[10px] shrink-0',
																	GEOCODE_COLORS[site.geocodeStatus] || 'bg-gray-100 text-gray-700',
																)}
															>
																{site.geocodeStatus}
															</Badge>
														)}
													</div>
												</div>
												{addressLine && (
													<div className="mt-1">
														{(() => {
															const hasCoords =
																site.latitude != null && site.longitude != null
															return (
																<button
																	type="button"
																	disabled={!hasCoords}
																	onClick={(e) => {
																		e.stopPropagation()
																		if (hasCoords) setMapSite(site)
																	}}
																	title={hasCoords ? 'Open map' : 'No coordinates available'}
																	className={cn(
																		'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs transition-colors max-w-full',
																		hasCoords
																			? 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 cursor-pointer'
																			: 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed',
																	)}
																>
																	<MapPin className="h-3 w-3 shrink-0" />
																	<span className="truncate">{addressLine}</span>
																</button>
															)
														})()}
													</div>
												)}
												<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
													{site.propertyType && <span>{site.propertyType}</span>}
													{site.sqft != null && <span>{site.sqft.toLocaleString()} sqft</span>}
													{site.latitude != null && site.longitude != null ? (
														<span>
															{site.latitude.toFixed(4)}, {site.longitude.toFixed(4)}
														</span>
													) : (
														site.approxLatitude != null &&
														site.approxLongitude != null && (
															// Marked as approximate so the number is never read as
															// the site's surveyed position.
															<span
																className="text-amber-600"
																title={
																	site.approxLabel
																		? `Approximate — centre of ${site.approxLabel}`
																		: 'Approximate location'
																}
															>
																≈ {site.approxLatitude.toFixed(4)},{' '}
																{site.approxLongitude.toFixed(4)}
															</span>
														)
													)}
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							)
						})}

						{/* Other Retail, for ONE candidate at a time.
						    The map shows every candidate's retailers together; this column
						    is where you read one site's in detail, so it needs a site
						    picker that the Locations map does not. */}
						{retailers.isAvailable && (
							<>
								{preciseSites.length > 1 && (
									<div className="border-t border-gray-200 pt-3">
										<label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
											Other Retail for
										</label>
										<select
											value={focusedSiteId ?? ''}
											onChange={(e) => setFocusedSiteId(e.target.value)}
											className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs focus:border-[#0080fe] focus:outline-none"
										>
											{preciseSites.map((s: any) => (
												<option key={s._id} value={s._id}>
													{s.name}
													{retailers.countsBySite[String(s._id)]
														? ` (${retailers.countsBySite[String(s._id)]})`
														: ''}
												</option>
											))}
										</select>
									</div>
								)}
								<OtherRetailPanel
									items={focusedRetailItems}
									isLoading={retailers.isLoading}
									onScan={retailers.canScanFocused ? retailers.openScan : undefined}
									onRemoveItem={retailers.removeItem}
									ringLimit={
										retailers.focusedItems.length > 0 ? retailers.focusedRingLimit : null
									}
								/>
							</>
						)}

						{/* Said plainly rather than left as an empty panel: a site placed by
						    its city centre has no meaningful "within a mile". */}
						{!retailers.isAvailable && approximateCount > 0 && (
							<p className="border-t border-gray-200 pt-3 text-xs leading-relaxed text-gray-400">
								Other Retail needs a site with a precise address. These sites are placed by
								city or postal code, so retail distances from them would not mean anything.
							</p>
						)}
					</div>

					{/* Right column — embedded sites map (5/6) */}
					<div className="col-span-5 relative rounded-lg overflow-hidden border border-gray-200">
						<MapContainer
							onMapLoad={(m) => {
								setMap(m)
								setIsMapLoaded(true)
							}}
						>
							<TradeAreaRings
								map={map}
								isMapLoaded={isMapLoaded}
								sites={ringSites}
								visibility={ringVisibility}
								visible={enabledRadii.length > 0}
							/>
							<OverlapZones
								map={map}
								isMapLoaded={isMapLoaded}
								sites={overlapSites}
								radii={enabledRadii}
								visible={showOverlaps && enabledRadii.length > 0}
							/>
							{showMarkers && <SiteMarkers map={map} sites={mappableSites} />}
							<DemographicHeatmap
								map={map}
								isMapLoaded={isMapLoaded}
								visible={showDemographics}
							/>
							{/* Every candidate's retailers at once, each already clipped to
							    its own site's rings by the hook. That union is the point of
							    this surface: the comparison is only visible if both sites'
							    trade areas are on screen together. */}
							<OtherRetailersLayer
								map={map}
								isMapLoaded={isMapLoaded}
								retailers={retailers.unionItems}
								visible={showOtherRetailers && retailers.isAvailable}
							/>
						</MapContainer>
						<div className="absolute top-4 left-4 z-20 flex flex-col gap-3 max-w-[240px]">
							{mappableSites.length > 0 && (
								<MapLegend
									sites={mappableSites}
									map={map}
									className="max-h-[50vh] overflow-y-auto"
								/>
							)}
							<MapLayersPanel
								showMarkers={showMarkers}
								onToggleMarkers={toggleMarkers}
								enabledRadii={enabledRadii}
								onToggleRadius={toggleRadius}
								showOverlaps={showOverlaps}
								onToggleOverlaps={toggleOverlaps}
								showDemographics={showDemographics}
								onToggleDemographics={toggleDemographics}
								showOtherRetailers={showOtherRetailers}
								onToggleOtherRetailers={
									retailers.isAvailable ? () => setShowOtherRetailers((v) => !v) : undefined
								}
								// The count is the union across candidates, not the focused
								// site's, because that is what the pills are describing.
								otherRetailersLabel={
									retailers.isAvailable && retailers.totalSaved > 0
										? `${retailers.unionItems.length}${
												enabledRadii.length ? ` · ${Math.max(...enabledRadii)} mi` : ''
											}`
										: undefined
								}
							/>
						</div>
						{(approximateCount > 0 || unplaceableCount > 0) && (
							<div className="absolute top-4 right-4 z-20 flex flex-col items-end gap-1">
								{approximateCount > 0 && (
									<div className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50/95 backdrop-blur border border-amber-200 px-2 py-1 rounded shadow-sm">
										<AlertCircle className="w-3 h-3" />
										{approximateCount} site{approximateCount === 1 ? '' : 's'} shown at
										approximate location{approximateCount === 1 ? '' : 's'} (dashed pin) — no
										trade area
									</div>
								)}
								{unplaceableCount > 0 && (
									<div className="inline-flex items-center gap-1.5 text-[11px] text-gray-600 bg-gray-50/95 backdrop-blur border border-gray-200 px-2 py-1 rounded shadow-sm">
										<AlertCircle className="w-3 h-3" />
										{unplaceableCount} site{unplaceableCount === 1 ? '' : 's'} hidden — no
										address or city to place {unplaceableCount === 1 ? 'it' : 'them'} by
									</div>
								)}
							</div>
						)}
						{mappableSites.length === 0 && (
							<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
								<div className="pointer-events-auto rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-4 py-3 max-w-sm text-center">
									<p className="text-sm text-gray-500">
										No sites placed yet — add an address, or at least a city or postal code,
										to see them on the map.
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Add Existing — same dialog shell as the portfolio picker. */}
			<Dialog open={addExistingOpen} onClose={() => setAddExistingOpen(false)}>
				<DialogContent className="max-w-[800px]">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<MapPin className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Add Existing Site</DialogTitle>
								<DialogDescription>
									Add a location from your catalogue, or a site you've already built in
									another evaluation
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<EvaluationAddExistingSitePopup evalId={evalId} />
					</div>
				</DialogContent>
			</Dialog>

			<EvaluationSiteForm
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				evalId={evalId}
			/>

			<SiteEditSheet
				open={!!editSheetSite}
				onClose={() => setEditSheetSite(null)}
				evalId={evalId}
				site={editSheetSite}
			/>

			{mapSite && (
				<MapLocationPopupCN
					open={!!mapSite}
					onClose={() => setMapSite(null)}
					location={{
						latitude: mapSite.latitude,
						longitude: mapSite.longitude,
						assetName: mapSite.name || '',
						address: mapSite.address || '',
						city: mapSite.city || '',
						stateProvince: mapSite.state || '',
						image: mapSite.location?.image,
					}}
				/>
			)}

			{retailers.isScanOpen && (
				<OtherRetailScanDialog
					open={retailers.isScanOpen}
					onClose={retailers.closeScan}
					anchorName={focusedSite?.name ?? null}
					onScan={retailers.runScan}
					onSave={retailers.saveSelection}
				/>
			)}

		</div>
	)
}

export default EvaluationSites
